import React, { useEffect, useRef, useState } from 'react';

import { faArrowLeftLong, faArrowRightLong } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Splide } from '@splidejs/react-splide';
import '@splidejs/splide/dist/css/splide.min.css';
import '@splidejs/splide/dist/css/splide.min.css';
import styled, { useTheme } from 'styled-components';

export const CarouselSliderContainer = styled.div`
  ${({ theme }) => `
  .splide__track {
    overflow: visible;
    height: 31.9rem;
    
    @media (max-width: ${theme.breakpoints.md}) {
      height: 32rem;
    }
  }

  .splide__pagination {
    transform: translate(0, 1.5rem);
    position: relative;
    display: flex;
    gap: 0.25rem;
  }

  .splide__pagination__page, .splide__pagination__page:hover {
    border-top: 14px solid ${theme.colors.white};
    border-bottom: 14px solid  ${theme.colors.white};
    background-color: ${theme.colors.black};
    opacity: 0.2;
    cursor: pointer;
    width: 3.5rem;
    height: 2rem;
    margin-left: 0;
    margin-right: 0;
    font-size: 0;
    line-height: 0;
    transition: all 0.3s;
    display: inline-block;
    position: relative;
    border-radius: 0;
    
    @media (max-width: ${theme.breakpoints.lg}) {
      width: 2rem;
    }
    
    @media (max-width: ${theme.breakpoints.md}) {
      width: 1.5rem;
    }
    
    @media (max-width: ${theme.breakpoints.sm}) {
      width: 1rem;
    }
  }

  .splide__pagination__page.is-active {
    transform: none;
    background-color: ${theme.colors.ttRed};
    opacity: 1;
  }
  
  .splide__slide {
    border-bottom: 4px solid transparent;
  }
  
  .splide__slide.is-active {
    border-bottom: 4px solid ${theme.colors.ttRed};
  }
`}
`;

const StyledArrowsContainer = styled.div`
  z-index: 20;
  grid-column-gap: 1rem;
  flex-direction: row;
  justify-content: flex-end;
  margin-top: 2rem;
  display: flex;
  position: relative;

  @media (max-width: ${(props) => props.theme.breakpoints.xs}) {
    justify-content: center;
  }
`;

const StyledArrow = styled.button`
  width: 3.5rem;
  height: 2rem;
  border: 1px solid black;
  color: black;
  cursor: pointer;
  background-color: rgba(0, 0, 0, 0);
  border-radius: 5rem;
  justify-content: center;
  align-items: center;
  font-family: ${(props) => props.theme.fonts.faSolid900};
  font-size: 1rem;
  transition: all 0.2s;
  display: flex;
  opacity: ${(props) => (props.disabled ? 0.4 : 1)};

  &:hover {
    border-color: ${(props) => props.theme.colors.primaryRed};
    color: ${(props) => props.theme.colors.primaryRed};
  }
`;

interface SplideInstance {
  index: number;
  length: number;
  options: {
    perPage?: number;
  };
}

interface CarouselSliderProps {
  children: React.ReactNode;
  pagination?: boolean;
}

const CarouselSlider: React.FC<CarouselSliderProps> = ({ children, pagination }) => {
  const [isPrevDisabled, setPrevDisabled] = useState(true);
  const [isNextDisabled, setNextDisabled] = useState(false);
  const splideRef = useRef<Splide | null>(null);
  const theme = useTheme();

  useEffect(() => {
    const splide = splideRef?.current?.splide;

    if (splide) {
      checkDisabled(splide);

      splide.on('moved', () => {
        checkDisabled(splide);
      });
    }
  }, []);

  const checkDisabled = (splide: SplideInstance) => {
    const currentIndex = splide.index;
    const perPage = splide.options.perPage ?? 1;
    setPrevDisabled(currentIndex === 0);
    setNextDisabled(currentIndex >= splide.length - perPage);
  };

  const goPrevious = () => {
    if (splideRef.current) {
      splideRef.current.go('<');
    }
  };

  const goNext = () => {
    if (splideRef.current) {
      splideRef.current.go('>');
    }
  };

  return (
    <CarouselSliderContainer>
      <Splide
        ref={splideRef}
        options={{
          perPage: 3,
          perMove: 1,
          start: 0,
          focus: 0,
          type: 'slide',
          gap: '2rem',
          arrows: false,
          pagination: pagination ?? false,
          speed: 600,
          dragAngleThreshold: 60,
          autoWidth: false,
          rewind: false,
          rewindSpeed: 400,
          waitForTransition: false,
          updateOnMove: true,
          omitEnd: !pagination,
          trimSpace: true,
          breakpoints: {
            [theme.breakpoints.values.lg]: {
              perPage: 2,
              gap: '3vw',
            },
            [theme.breakpoints.values.md]: {
              perPage: 1,
              gap: '1.5vw',
            },
          },
        }}
      >
        {children}
      </Splide>
      {!pagination && (
        <StyledArrowsContainer className='splide-arrows'>
          <StyledArrow disabled={isPrevDisabled} onClick={goPrevious} className='splide-arrow splide-arrow--prev'>
            <FontAwesomeIcon icon={faArrowLeftLong} />
          </StyledArrow>
          <StyledArrow disabled={isNextDisabled} onClick={goNext} className='splide-arrow splide-arrow--next'>
            <FontAwesomeIcon icon={faArrowRightLong} />
          </StyledArrow>
        </StyledArrowsContainer>
      )}
    </CarouselSliderContainer>
  );
};

export default CarouselSlider;
